import type { AppProps as NextAppProps } from 'next/app';
import { AuthenticationContextProvider } from '3-foundation/Authentication/AuthenticationContext';
import { PeriodicalProvider } from 'context/PeriodicalContext';
import 'ui/styles/globals.scss';
import { StrictMode, useEffect } from 'react';
import Application from '3-foundation/Application/ApplicationEntity';
import { ApplicationContextProvider } from '3-foundation/Application/ApplicationContext';

// modified version - allows for custom pageProps type, falling back to 'any'
// https://stackoverflow.com/questions/64722812/what-typescript-type-should-nextjs-app-tsx-component-and-pageprops-be
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AppProps<P = any> = {
	pageProps: P;
} & Omit<NextAppProps<P>, 'pageProps'>;

const App = ({ Component, pageProps }: AppProps) => {
	const { session, application } = pageProps ?? {};

	/**
	 * This useEffect transfers application values from pageProps to
	 * a client-side exposed instance of the Application entity that can be
	 * accessed in connectors, repositories and other non React client-side modules
	 *
	 * If you want to access the values inside a React module you can use the
	 * ApplicationContext
	 */
	useEffect(() => {
		if (application?.url) Application.setUrl(application.url);
		if (application?.locale) Application.setLocale(application.locale);
		if (application?.envVars) Application.envVars = application.envVars;
	}, [application]);

	return (
		<StrictMode>
			<ApplicationContextProvider application={application}>
				<AuthenticationContextProvider session={session}>
					<PeriodicalProvider>
						<Component {...pageProps} />
					</PeriodicalProvider>
				</AuthenticationContextProvider>
			</ApplicationContextProvider>
		</StrictMode>
	);
};

export default App;
